import { CalendarMonthOutlined } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Grid, IconButton, Popover, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';

import FilterSidebar from '../../components/common/filters/FilterSidebar';
import { FilterState } from '../../components/common/filters/interfaces';
import ListHeader from '../../components/listHeader/ListHeader';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useGetEmployeeTimeOffAvailability } from '../../services/react-query/employee_time_offs/useGetEmployeeTimeOffAvailability';
import { useGetScheduledEmployeeEvents } from '../../services/react-query/employees';
import { usePublishEvents } from '../../services/react-query/events';
import { useGetAllPreferences } from '../../services/react-query/preferences';
import { headerTypographyProps, listPageLayoutStyles } from '../../styles/sxStyling';
import { AttendanceStatus, Event } from '../../types/Event';
import { PREFERENCES_VALID_KEYS } from '../../types/Preference';
import { UserConfigurableModule } from '../../types/User';
import { Loading } from '../../uikit';
import { calcEventCols } from '../../utility/availability';
import { humanizeDateRange } from '../../utility/dateUtils';
import { EmployeesScheduleFilters } from '../../utility/employee';
import DatesHeader from './DatesHeader';
import {
  calendarScrollableContentSx,
  calendarToolbarSx,
  calendarWrapperSx,
  loadingContainerSx,
  toolbarDateRangeSx,
} from './employeesScheduleStyles';
import { PublishConfirmDialog } from './PublishConfirmDialog';
import ScheduleRow, { IScheduleRowProps } from './ScheduleRow';

export interface IEventData {
  event: Event;
  attendanceStatus: AttendanceStatus;
  checkedIn: string | null,
  checkedOut: string | null,
  startCol: number;
  endCol: number;
}

// Generates ISO date keys for the current week used by availability lookups.
const getWeekDateKeys = (weekStart: DateTime, dayCount: number): string[] => _.times(dayCount, (dayOffset) => weekStart.plus({ days: dayOffset }).toISODate() ?? '');

const EmployeesSchedule: React.FC = () => {
  const { isMobile } = useIsMobile();

  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const [selectedDate, setSelectedDate] = useState<DateTime<true>>(DateTime.now().startOf('week'));
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [dayCount, setDayCount] = useState(7);

  const weekStart = useMemo(() => dayCount < 5 ? selectedDate.startOf('day') : selectedDate.startOf('week'), [selectedDate, dayCount]);
  const weekEnd = useMemo(() => weekStart.plus({ days: dayCount - 1 }).endOf('day'), [weekStart, dayCount]);
  const availabilityEndDate = useMemo(() => weekStart.plus({ days: dayCount }), [weekStart, dayCount]);
  const weekDateKeys = useMemo(() => getWeekDateKeys(weekStart, dayCount), [weekStart, dayCount]);

  const { mutate: publishEvents, isPending: isPublishing } = usePublishEvents();

  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE, PREFERENCES_VALID_KEYS.EMPLOYEE_TITLE],
      },
    },
  });

  const {
    data: employeeEvents,
    error: employeeEventsError,
    isFetching: isFetchingEmployeeEvents,
  } = useGetScheduledEmployeeEvents({
    params: {
      date_range: [weekStart.toISO(), weekEnd.toISO()],
      filters: filterState,
    },
  });

  const employeeUuids = useMemo(() => _.map(employeeEvents?.employees ?? [], 'uuid'), [employeeEvents]);

  const { data: availabilityData, isFetching: isFetchingAvailability } = useGetEmployeeTimeOffAvailability({
    params: {
      start_date: weekStart.toISODate(),
      end_date: availabilityEndDate.toISODate(),
      employee_uuids: employeeUuids,
    },
    enabled: !_.isEmpty(employeeUuids),
  });

  const isLoadingSchedule = isFetchingEmployeeEvents || isFetchingAvailability;

  const rowData: IScheduleRowProps[] = useMemo(() => {
    if (!employeeEvents || isFetchingEmployeeEvents) return [];

    const eventsByEmployeeUuid = _.groupBy(employeeEvents.event_employees, 'employee_uuid');

    const availabilityByEmployee = availabilityData ?? {};

    return _.map(employeeEvents.employees, (employee) => {
      const employeeEventAssignments = _.uniqBy(eventsByEmployeeUuid[employee.uuid] ?? [], eea => eea.event.uuid);

      const events = _(employeeEventAssignments)
        .map(
          (employeeEvent): IEventData => ({
            event: employeeEvent.event,
            attendanceStatus: employeeEvent.status,
            checkedIn: employeeEvent.checked_in_at,
            checkedOut: employeeEvent.checked_out_at,
            ...calcEventCols(weekStart, employeeEvent.event),
          })
        )
        .sortBy(['startCol', 'endCol'])
        .value();

      return {
        employeeUuid: employee.uuid,
        employeeLabel: employee.display_name,
        employeeAvatarUrl: employee.avatar_url,
        weekDateKeys,
        availabilityByDate: availabilityByEmployee[employee.uuid] ?? {},
        events,
      };
    });
  }, [employeeEvents, isFetchingEmployeeEvents, availabilityData, weekDateKeys, weekStart]);

  const handlePublish = () => {
    setPublishDialogOpen(true);
  };

  const handleConfirmPublish = () => {
    const eventUuids = _.uniq(_.map(employeeEvents?.event_employees ?? [], ({ event }) => event.uuid));

    publishEvents(eventUuids);
    setPublishDialogOpen(false);
  };

  if (isMobile) {
    return (
      <Box sx={{ alignContent: 'center', height: '100%' }}>
        <Typography {...headerTypographyProps} whiteSpace="wrap" textAlign="center">
          We&apos;re not able to correctly show this page at this size.
          <br />
          <br />
          Please use a device with a larger screen.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ListHeader title="Employees Schedule" rightActions={null} />

      <Box sx={listPageLayoutStyles}>
        <FilterSidebar
          moduleFilters={EmployeesScheduleFilters(preferenceData ?? [])}
          module={UserConfigurableModule.EMPLOYEES_SCHEDULE}
          showSearch={false}
          activeFilter={filterState}
          setActiveFilter={setFilterState}
        />

        <Box sx={calendarWrapperSx}>
          <Box sx={calendarToolbarSx}>
            <ButtonGroup size="small">
              <Button sx={{ height: '24px' }} onClick={() => setSelectedDate(selectedDate.minus({ days: dayCount }))}>
                Prev
              </Button>
              <Button sx={{ height: '24px' }} onClick={() => setSelectedDate(DateTime.now().startOf('day'))}>
                Today
              </Button>
              <Button sx={{ height: '24px' }} onClick={() => setSelectedDate(selectedDate.plus({ days: dayCount }))}>
                Next
              </Button>
            </ButtonGroup>

            <Box sx={toolbarDateRangeSx}>
              <Typography variant="h5" fontSize="0.9rem">
                {humanizeDateRange(weekStart.toISO(), weekEnd.toISO())}
              </Typography>

              <IconButton size="small" onClick={(event) => setDatePickerAnchor(event.currentTarget)}>
                <CalendarMonthOutlined fontSize="small" />
              </IconButton>
            </Box>

            <Popover
              open={Boolean(datePickerAnchor)}
              anchorEl={datePickerAnchor}
              onClose={() => setDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <DateCalendar
                value={selectedDate}
                onChange={(picked) => {
                  if (picked) setSelectedDate(picked);
                  setDatePickerAnchor(null);
                }}
              />
            </Popover>

            <Box sx={{ display: 'flex', textWrap: 'nowrap', gap: 1, alignItems: 'center' }}>
              <ButtonGroup size="small">
                <Button sx={{ height: '24px' }} onClick={() => setDayCount(7)}>
                  Week
                </Button>
                <Button sx={{ height: '24px' }} onClick={() => setDayCount(3)}>
                  3 Days
                </Button>
              </ButtonGroup>
              <Button size="small" variant="contained" onClick={handlePublish} disabled={isPublishing}>
                Publish
              </Button>
            </Box>
          </Box>

          {employeeEventsError ? (
            <Typography align="center" variant="h6" sx={{ pt: 6 }}>
              {employeeEventsError.message}
            </Typography>
          ) : (
            <Box sx={calendarScrollableContentSx}>
              <Grid container columns={dayCount + 1} sx={{ gridTemplateColumns: `minmax(50px, 200px) repeat(${dayCount}, 1fr)` }}>
                <DatesHeader weekStart={weekStart} dayCount={dayCount} />

                {_.map(rowData, (row) => (
                  <ScheduleRow key={row.employeeUuid} {...row} />
                ))}
              </Grid>

              {isLoadingSchedule && (
                <Box sx={loadingContainerSx}>
                  <Loading />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <PublishConfirmDialog open={publishDialogOpen} isPublishing={isPublishing} onClose={() => setPublishDialogOpen(false)} onConfirm={handleConfirmPublish} />
    </Box>
  );
};

export default EmployeesSchedule;
